import './index.css'
import get from 'lodash/get'
import * as React from 'react'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import validation from 'react-validation-mixin'
import strategy from 'react-validatorjs-strategy'
import type { PasswordStrength } from 'types/business'
import type { LoginDetails } from 'types/user'
import {
  loadMfaMethods,
  loginUser,
  updateUserSettings,
} from '@disco/state-management/actions/UserActions'
import getReferrer from '@disco/utils/getReferrer'
import { checkPasswordSecurity } from '@disco/utils/password'
import i18next from '@disco/i18n/i18next'
import DiscoLogoSvg from 'assets/images/disco-logo.svg'
import DocumentHeadTags from 'components/document-head-tags'
import WeakPasswordError from 'components/password/weak-password-error'
import InlineError from 'components/ui/inline-error'
import {
  Button,
  Checkbox,
  Flex,
  Heading,
  PasswordTextField,
  Select,
  Strong,
  Text,
  TextField,
} from 'components/ds'
import LoginError from 'components/utils/login-error'
import type { UserReducer } from '@disco/state-management/reducers/user'
import type { HttpErrorType } from 'types/errors'
type StateProps = {
  user: UserReducer
  passwordStrength: PasswordStrength
}
type OwnProps = object
type Actions = {
  loginUser: typeof loginUser
  updateUserSettings: typeof updateUserSettings
  loadMfaMethods: typeof loadMfaMethods
}
type Validation = {
  getValidationMessages: (key: string) => {
    toString(): string
  }
  validate: (arg0?: (error: Error) => unknown) => void
}
type Props = StateProps &
  Actions &
  OwnProps &
  Validation & { navigate: NavigateFunction }

type State = {
  loadingState: 'LOADING' | 'ERROR' | 'SUCCESS'
  submitting: boolean
  first_name: string
  last_name: string
  email: string
  password: string
  language: string
  loadingError: HttpErrorType | null | undefined
  securityRequirementsError: React.ReactNode | null | undefined
  securityBreachError: boolean
  marketingOptinCheckbox: boolean
}

class PostInvites extends React.Component<Props, State> {
  state: State = {
    loadingState: 'SUCCESS',
    submitting: false,
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    language: i18next.language || 'en',
    loadingError: null,
    securityRequirementsError: null,
    securityBreachError: false,
    marketingOptinCheckbox: true,
  }
  validatorTypes = strategy.createSchema({
    first_name: 'required|max:30',
    last_name: 'max:30',
    // no need for password confirmation
    password: 'required',
  })

  componentDidMount() {
    // this._loadUser()
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (this.state.loadingState === 'LOADING' && nextProps.user.is_active) {
      const { first_name, last_name, email } = nextProps.user
      this.setState({
        loadingState: 'SUCCESS',
        first_name,
        last_name,
        email,
      })
    } else if (this.state.loadingState === 'LOADING' && nextProps.user.error) {
      this.setState({
        loadingState: 'ERROR',
        loadingError: nextProps.user.error,
      })
    }
  }

  async _loadUser() {
    const loginDetails: LoginDetails = {
      referer: getReferrer(),
    }
    const returnPath = false // keep user here

    this.props.loginUser(loginDetails, returnPath)
  }

  getValidatorData = () => {
    const { first_name, last_name, password } = this.state
    return {
      first_name,
      last_name,
      password,
    }
  }
  _handleLanguageChange = (value: string) => {
    this.setState({ language: value })
    i18next.changeLanguage(value)
  }

  _checkErrors = () => {
    const passwordSecure = checkPasswordSecurity(
      this.state.password,
      this.props.passwordStrength,
    )

    if (!passwordSecure) {
      this.setState({
        securityRequirementsError: (
          <WeakPasswordError passwordStrength={this.props.passwordStrength} />
        ),
      })
      return
    } else {
      this.setState({
        securityRequirementsError: null,
      })
    }

    this.props.validate()
  }
  _handleSubmit = async evt => {
    evt.stopPropagation()

    this._checkErrors()

    this.props.validate(async error => {
      if (error) return
      this.setState({
        submitting: true,
      })

      try {
        const {
          first_name,
          last_name,
          email,
          password,
          marketingOptinCheckbox,
        } = this.state

        try {
          await this.props.updateUserSettings({
            first_name,
            last_name,
            email,
            new_password: password,
            opt_in: marketingOptinCheckbox,
          })
        } catch (e) {
          if (e?.body?.code === 'breached_password') {
            this.setState({
              securityBreachError: true,
            })
          }

          throw e
        }

        // TODO: check
        if (
          get(
            this.props.user,
            'business.private_metadata.mfa_auth_enabled',
            false,
          )
        ) {
          this.props.loadMfaMethods()
        } else {
          // Navigation will be handled by the container
          this.props.navigate('/')
        }
      } catch (err) {
        console.error(err)
        this.setState({
          submitting: false,
        })
      }
    })
  }
  _handleInputChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
    field: keyof State,
  ) => {
    this.setState(prevState => ({
      ...prevState,
      [field]: evt.target.value,
    }))
    this.props.validate()
  }

  _renderFields() {
    return (
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="2">
          <Text as="label" size="3" weight="medium">
            First name
          </Text>
          <TextField.Root
            size="3"
            placeholder="John"
            name="first_name"
            value={this.state.first_name}
            onChange={evt => this._handleInputChange(evt, 'first_name')}
          />
          {!!this.props.getValidationMessages('first_name').toString() && (
            <Text size="1" color="red">
              {this.props.getValidationMessages('first_name').toString()}
            </Text>
          )}
        </Flex>
        <Flex direction="column" gap="2">
          <Text as="label" size="3" weight="medium">
            Last name
          </Text>
          <TextField.Root
            size="3"
            placeholder="(optional)"
            name="last_name"
            value={this.state.last_name}
            onChange={evt => this._handleInputChange(evt, 'last_name')}
          />
        </Flex>
        <Flex direction="column" gap="2">
          <Text as="label" size="3" weight="medium">
            Email
          </Text>
          <TextField.Root size="3" value={this.state.email} readOnly disabled />
        </Flex>
        <Flex direction="column" gap="2">
          <Text as="label" size="3" weight="medium">
            Set your password
          </Text>
          <PasswordTextField
            size="3"
            placeholder="Enter password"
            autoComplete="new-password"
            value={this.state.password}
            strength={this.props.passwordStrength}
            onChange={evt => {
              this.setState({
                password: evt.target.value,
                securityBreachError: false,
                securityRequirementsError: null,
              })
              this.props.validate()
            }}
          />
          <InlineError error={this.state.securityRequirementsError} />
        </Flex>
        <Flex direction="column" gap="2">
          <Text as="label" size="3" weight="medium">
            Select language
          </Text>
          <Select.Root
            size="3"
            value={this.state.language}
            onValueChange={this._handleLanguageChange}
          >
            <Select.Trigger
              variant="soft"
              className="PostInvites_LanguageSelect"
            />
            <Select.Content>
              <Select.Item value="en">English</Select.Item>
              <Select.Item value="ja">Japanese</Select.Item>
            </Select.Content>
          </Select.Root>
        </Flex>
        <Text as="p" size="1">
          {`By clicking "Get started" you agree to have a DISCO profile created and be bound by our `}
          <a
            className="PostInvites_Link"
            href="https://disco.ac/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms
          </a>
          {' and '}
          <a
            className="PostInvites_Link"
            href="https://disco.ac/security"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </Text>
        <Flex align="center" gap="2" asChild>
          <label>
            <Checkbox
              checked={this.state.marketingOptinCheckbox}
              onCheckedChange={checked => {
                this.setState({ marketingOptinCheckbox: Boolean(checked) })
              }}
            />
            <Text size="1">
              {"It's ok to email me about the DISCO service."}
            </Text>
          </label>
        </Flex>
      </Flex>
    )
  }

  _renderSubmit() {
    const { securityRequirementsError, password, submitting } = this.state
    const { getValidationMessages } = this.props
    const submitEnabled =
      !securityRequirementsError &&
      password &&
      !getValidationMessages('first_name').toString() &&
      !getValidationMessages('last_name').toString() &&
      !getValidationMessages('email').toString()
    return (
      <Button
        size="3"
        color="gray"
        highContrast
        className="PostInvites_SubmitBtn"
        disabled={!submitEnabled || submitting}
        onClick={this._handleSubmit}
      >
        {submitting ? 'Creating account…' : 'Get started'}
      </Button>
    )
  }

  _renderServerError() {
    const { error } = this.props.user
    if (!error) return null
    return (
      <LoginError
        passwordStrength={this.props.passwordStrength}
        error={error}
      />
    )
  }

  render() {
    if (this.state.loadingState === 'LOADING') {
      return (
        <div className="PostInvites_Content">
          <h1 className="Form_Header PostInvites_Header">
            Create your DISCO profile
          </h1>
          Authorizing...
        </div>
      )
    }

    if (this.state.loadingState === 'ERROR') {
      return (
        <div className="PostInvites_Content">
          <h1 className="Form_Header PostInvites_Header">
            Create your DISCO profile
          </h1>
          {this.state.loadingError && (
            <LoginError
              passwordStrength={this.props.passwordStrength}
              errorContext="link"
              error={this.state.loadingError}
              suggestContactingSupport
            />
          )}
        </div>
      )
    }

    return (
      <div className="PostInvites_Content">
        <DocumentHeadTags title="Create profile | DISCO" />
        <DiscoLogoSvg className="PostInvites_Logo" />
        <div className="PostInvites_Container">
          <Heading size="6" mt="0" color="gray" highContrast>
            Accept your invitation
          </Heading>
          {this.props.user.business?.name && (
            <Text as="p" size="2">
              <Strong>{this.props.user.business.name}</Strong>
              {' has invited you to join their DISCO'}
            </Text>
          )}
          {this._renderFields()}
          {this._renderSubmit()}
          {this._renderServerError()}
        </div>
      </div>
    )
  }
}

const Wrapped = validation(strategy)(PostInvites) as any

// HOC to provide navigate function to class component
const PostInvitesWithNavigate = props => {
  const navigate = useNavigate()
  return <Wrapped {...props} navigate={navigate} />
}

export default PostInvitesWithNavigate as any
